import { useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { INTL_LOCALES, toLocale } from '@/shared/lib/i18n';
import { SheetFrame, SheetFrameBody, SheetFrameHeading } from '@/shared/ui';
import { flameTier } from '../model/streakTier';
import type { PracticeDay, StreakSummary } from '../model/types';
import { Flame } from './Flame';

/** Насколько далеко назад листается календарь — столько, сколько отдаёт сервер (~400 дней). */
const MAX_MONTHS_BACK = 13;

/**
 * Календарь одного цвета: день с выполненной целью — фиолетовый Periwinkle
 * из палитры ролевых игр (`entities/practice/model/palette.ts`), тем же
 * градиентом, что у карточек, какой бы ни была ступень огня: цвет огня
 * говорит о длине серии, ячейки — только о том, засчитан ли день. Тон
 * пастельный, поэтому цифра тёмная, не белая.
 */
const MET_DAY = 'bg-gradient-to-b from-[#C7D2FE] to-[#E1E7FF] text-[#3730a3]';
const TODAY_RING = 'ring-2 ring-[#8b9cf7] ring-offset-2 ring-offset-[var(--color-glass)]';

interface StreakSheetProps {
  streak: StreakSummary;
  onClose: () => void;
}

/**
 * Шторка стрика по референсу Дмитрия: крестик слева, большой огонь с числом,
 * «Дней подряд» и календарь месяца. Цвет огня — ступень длины стрика
 * (`streakTier`), дни с выполненной целью — один фиолетовый, сегодня — кольцо. Своя шапка, не `shared/ui/Sheet`: в референсе у шторки
 * нет заголовка в строку с крестиком.
 */
export function StreakSheet({ streak, onClose }: StreakSheetProps) {
  const { t, i18n } = useTranslation('DailyGoalBar');
  const intlLocale = INTL_LOCALES[toLocale(i18n.language)];

  const [todayYear, todayMonth] = streak.today.split('-').map(Number) as [number, number];
  // Лента месяцев слева направо, от самого старого к текущему: листается
  // пальцем со снапом по месяцу, как heatmap на «Аналитике», стрелки —
  // тот же скролл на ширину ленты.
  const months = Array.from(
    { length: MAX_MONTHS_BACK + 1 },
    (_, index) => new Date(todayYear, todayMonth - 1 - MAX_MONTHS_BACK + index, 1),
  );
  const stripRef = useRef<HTMLDivElement>(null);
  const [shownIndex, setShownIndex] = useState(months.length - 1);
  const shown = months[shownIndex] ?? months[months.length - 1]!;

  // До первой отрисовки — сразу на текущем месяце, без проезда через год.
  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (strip) strip.scrollLeft = strip.scrollWidth;
  }, []);

  const onStripScroll = () => {
    const strip = stripRef.current;
    if (!strip || strip.clientWidth === 0) return;
    setShownIndex(Math.round(strip.scrollLeft / strip.clientWidth));
  };

  const scrollMonths = (direction: -1 | 1) => {
    const strip = stripRef.current;
    strip?.scrollBy({ left: direction * strip.clientWidth, behavior: 'smooth' });
  };

  const byDay = new Map<string, PracticeDay>(streak.days.map((day) => [day.day, day]));
  const todayTier = flameTier(streak.streak, streak.todaySeconds, streak.goalMinutes);
  const todayMet = todayTier > 0;

  const subtitle =
    streak.streak === 0 ? t('startStreak') : todayMet ? t('goalMetToday') : t('keepStreak');

  return (
    <SheetFrame onClose={onClose} dialogClassName="pb-[calc(var(--safe-bottom)+1.25rem)]">
            <div className="flex shrink-0 items-center pt-3">
              <button
                type="button"
                aria-label={t('close')}
                onClick={onClose}
                className="flex size-9 items-center justify-center rounded-full bg-glass-2 text-ink active:bg-glass-3"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>

            <SheetFrameBody className="flex flex-col items-center">
              <Flame tier={todayTier} className="h-28 w-24">
                <span className="text-4xl font-extrabold tabular-nums">{streak.streak}</span>
              </Flame>

              <SheetFrameHeading className="mt-3 text-3xl font-extrabold text-ink">
                {t('dayStreak', { count: streak.streak })}
              </SheetFrameHeading>
              <p className="mt-1 text-center text-[15px] text-ink-muted">{subtitle}</p>

              <div className="mt-4 flex w-full items-center justify-between">
                <button
                  type="button"
                  aria-label={t('prevMonth')}
                  disabled={shownIndex === 0}
                  onClick={() => scrollMonths(-1)}
                  className="flex size-9 items-center justify-center rounded-full text-ink-muted active:bg-glass-2 disabled:opacity-30"
                >
                  <ChevronLeft className="size-5" aria-hidden="true" />
                </button>
                <span className="text-lg font-bold text-ink">{monthTitle(shown, intlLocale)}</span>
                <button
                  type="button"
                  aria-label={t('nextMonth')}
                  disabled={shownIndex === months.length - 1}
                  onClick={() => scrollMonths(1)}
                  className="flex size-9 items-center justify-center rounded-full text-ink-muted active:bg-glass-2 disabled:opacity-30"
                >
                  <ChevronRight className="size-5" aria-hidden="true" />
                </button>
              </div>

              {/* Только горизонтальный жест: `Drawer.Body` сам ставит `touch-action:
                  pan-y`, а кольцо «сегодня» вылезало за нижний край ленты, и
                  палец на календаре двигал её ещё и по вертикали. `touch-pan-x`
                  отсекает вертикаль внутри ленты, `overflow-y-hidden` — сам
                  вертикальный скролл, отступ внутри месяца — место под кольцо.
                  `snap-always` — один свайп = один месяц, без проскока.
                  `shrink-0` — иначе тело шторки (flex-колонка) сжимало ленту
                  по высоте, и календарь уезжал во внутренний вертикальный скролл. */}
              <div
                ref={stripRef}
                onScroll={onStripScroll}
                className="scroll-area flex w-full shrink-0 touch-pan-x snap-x snap-mandatory items-start overflow-x-auto overflow-y-hidden overscroll-x-contain"
              >
                {months.map((month) => (
                  <div key={month.getTime()} className="w-full shrink-0 snap-start snap-always p-1">
                    <MonthGrid
                      year={month.getFullYear()}
                      month={month.getMonth()}
                      today={streak.today}
                      byDay={byDay}
                      intlLocale={intlLocale}
                    />
                  </div>
                ))}
              </div>
            </SheetFrameBody>
    </SheetFrame>
  );
}

/**
 * «Сентябрь 2026», а не `month: 'long', year: 'numeric'` целиком: в русской
 * локали тот даёт «сентябрь 2026 г.» — с хвостом «г.», которого в заголовке
 * календаря не пишут.
 */
function monthTitle(date: Date, intlLocale: string): string {
  const month = new Intl.DateTimeFormat(intlLocale, { month: 'long' }).format(date);
  return `${month.charAt(0).toLocaleUpperCase(intlLocale)}${month.slice(1)} ${date.getFullYear()}`;
}

interface MonthGridProps {
  year: number;
  /** 0–11, как у `Date`. */
  month: number;
  today: string;
  byDay: Map<string, PracticeDay>;
  intlLocale: string;
}

/** 2024-01-01 — понедельник: от него берутся подписи дней недели в локали. */
const A_MONDAY = new Date(2024, 0, 1);

function MonthGrid({ year, month, today, byDay, intlLocale }: MonthGridProps) {
  const weekdayFormat = new Intl.DateTimeFormat(intlLocale, { weekday: 'short' });
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    weekdayFormat.format(new Date(A_MONDAY.getFullYear(), 0, 1 + index)),
  );

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Пн = 0 … Вс = 6 (у `getDay()` воскресенье — 0).
  const leading = (new Date(year, month, 1).getDay() + 6) % 7;
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

  return (
    <div className="mt-3 grid w-full grid-cols-7 gap-x-1.5 gap-y-2">
      {weekdays.map((label) => (
        <span key={label} className="pb-1 text-center text-xs font-semibold text-ink-subtle capitalize">
          {label.replace('.', '')}
        </span>
      ))}
      {Array.from({ length: leading }, (_, index) => (
        <span key={`gap-${index}`} />
      ))}
      {Array.from({ length: daysInMonth }, (_, index) => {
        const dayNumber = index + 1;
        const key = `${monthKey}-${String(dayNumber).padStart(2, '0')}`;
        const practice = byDay.get(key);
        const met = practice !== undefined && practice.seconds >= practice.goalSeconds;
        const isToday = key === today;
        const isFuture = key > today;

        return (
          <span
            key={key}
            className={cn(
              'mx-auto flex aspect-square w-full max-w-11 items-center justify-center rounded-full text-[15px] font-semibold tabular-nums',
              met ? MET_DAY : 'bg-glass text-ink-muted',
              isFuture && 'opacity-50',
              isToday && TODAY_RING,
            )}
          >
            {dayNumber}
          </span>
        );
      })}
    </div>
  );
}
